import { Component, ElementRef, HostBinding, NgZone, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";

import { SettingsService } from "./core/settings/settings.service";
import { ViewportService } from "./core/viewport/viewport.service";
import { AuthService } from "./routes/services/auth.service";
import { StorageService, localValue } from "./routes/services/storage.service";
import { PermissionsService } from "./routes/services/permissions.service";
import { Menu } from "./routes/menu";
import { MenuService } from "./core/menu/menu.service";
import { fromEvent, Subject, Subscription } from "rxjs";
import { auditTime, takeUntil } from "rxjs/operators";
import { submitCycle } from "./routes/Clients/cycles-plantings/interfaces/cycle.interface";
import { CyclesService } from "./routes/Clients/cycles-plantings/services/cycles.service";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"]
})
export class AppComponent implements OnInit, OnDestroy {
  @HostBinding("class.layout-fixed") get isFixed() {
    return this.settings.getLayoutSetting("isFixed");
  }
  // Los modos colapsados son solo de escritorio: en compacto el menu es un panel flotante
  @HostBinding("class.aside-collapsed") get isCollapsed() {
    return this.settings.getLayoutSetting("isCollapsed") && !this.viewport.isCompact;
  }
  @HostBinding("class.layout-boxed") get isBoxed() {
    return this.settings.getLayoutSetting("isBoxed");
  }
  @HostBinding("class.layout-fs") get useFullLayout() {
    return this.settings.getLayoutSetting("useFullLayout");
  }
  @HostBinding("class.offsidebar-open") get offsidebarOpen() {
    return this.settings.getLayoutSetting("offsidebarOpen");
  }
  @HostBinding("class.offsidebar-closed") get offsidebarClosed() {
    return !this.settings.getLayoutSetting("offsidebarOpen");
  }
  @HostBinding("class.aside-collapsed-text") get isCollapsedText() {
    return this.settings.getLayoutSetting("isCollapsedText") && !this.viewport.isCompact;
  }
  // Menu lateral abierto en telefonos/tablets (ver "Layout compacto" en layout.scss)
  @HostBinding("class.aside-mobile-open") get isAsideMobileOpen() {
    return this.settings.getLayoutSetting("asideMobileOpen") && this.viewport.isCompact;
  }
  modeSubsciption: Subscription | undefined;
  farmSuscription: Subscription | undefined;
  cycleSuscription: Subscription | undefined;
  currentZoom = 1;
  private destroy$ = new Subject<void>();

  constructor(
    public settings: SettingsService,
    public route: Router,
    public AuthService: AuthService,
    public storageService: StorageService,
    public permissionsService: PermissionsService,
    public menuService: MenuService,
    public cyclesService: CyclesService,
    public router: Router,
    private ngZone: NgZone,
    private viewport: ViewportService,
    private elementRef: ElementRef<HTMLElement>
  ) {}

  /** Cancela las suscripciones del componente raiz, incluido el resize de la ventana. */
  ngOnDestroy(): void {
    if (this.modeSubsciption) {
      this.modeSubsciption.unsubscribe();
    }
    if (this.cycleSuscription) {
      this.cycleSuscription.unsubscribe();
    }
    if (this.farmSuscription) {
      this.farmSuscription.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Aplica un zoom leve solo entre 1400px y 1800px de ancho de ventana (px CSS),
   * para que el contenido completo sea visible sin recortes en esas resoluciones.
   * Usa "innerWidth" y no "screen.width": con escalado del sistema (p. ej. 125%)
   * Chrome en Linux reporta "screen.width" en px fisicos (1920) y Firefox en px
   * CSS (1536), asi que el zoom solo se aplicaba en Firefox. "innerWidth" es igual
   * en ambos, coincide con las media queries y no cambia al aplicar "zoom" CSS.
   *
   * El zoom va en <app-root> y no en <html>: asi los popups que se agregan al
   * <body> (dropdowns, popovers, tooltips, modales) quedan fuera del zoom y
   * ng-bootstrap los posiciona bien en todos los navegadores (ver AppModule).
   */
  applyResolutionZoom = () => {
    const viewportWidth = window.innerWidth;
    const zoom = viewportWidth >= 1400 && viewportWidth <= 1800 ? 0.8 : 1;

    if (zoom !== this.currentZoom) {
      (this.elementRef.nativeElement.style as any).zoom = zoom === 1 ? "" : zoom;
      this.currentZoom = zoom;
    }

    // Con zoom, un "vh" dentro del documento se ve multiplicado por el zoom, asi que
    // "100vh" ya no llena la ventana. Se publica "--viewport-height" con el alto
    // real compensado (100 / zoom) y los estilos lo usan en vez de "100vh":
    //   .section-container (layout.scss), #wrapper del estanque, mapa de la granja
    //   y Barrido de testeos. Sin zoom se quita y aplica el valor por defecto.
    const root = document.documentElement;
    if (zoom === 1) {
      root.style.removeProperty("--viewport-height");
    } else {
      // "dvh" con respaldo a "vh" en navegadores que no lo soportan (Safari < 15.4)
      const unit = window.CSS && CSS.supports("height", "1dvh") ? "dvh" : "vh";
      root.style.setProperty("--viewport-height", `${100 / zoom}${unit}`);
    }
  };

  /** Inicializa el menu segun modo/granja/ciclo y aplica el zoom por resolucion. */
  ngOnInit() {
    this.modeSubsciption = this.storageService.watchStorage("mode").subscribe((mode) => {
      const farm_id = mode === 1 ? this.storageService.localValue.farm_id : null;
      this.setMenu({
        farm_id,
        redirect: true,
        validate_cycle: true
      });
    });

    this.farmSuscription = this.storageService.watchStorage("farmId").subscribe((farm_id) => {
      this.setMenu({
        farm_id,
        redirect: false,
        validate_cycle: true
      });
    });

    this.cycleSuscription = this.storageService.watchStorage("cycle").subscribe((cycle) => {
      this.setMenu({
        farm_id: this.storageService.localValue.farm_id,
        redirect: true,
        validate_cycle: true
      });
    });

    /* Evita que los enlaces sin destino (href vacío o "#...") recarguen la página. Los enlaces con
       destino real (redes sociales, mailto, tel) deben abrirse; antes también se bloqueaban */
    document.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      const href = target.getAttribute("href");
      if (target.tagName === "A" && (!href || href.startsWith("#"))) e.preventDefault();
    });

    this.applyResolutionZoom();
    // El zoom solo toca estilos del DOM, asi que el resize se escucha fuera de la zona
    // de Angular para no disparar deteccion de cambios de toda la app en cada evento,
    // y se limita a una ejecucion cada 100ms mientras se redimensiona.
    this.ngZone.runOutsideAngular(() => {
      fromEvent(window, "resize")
        .pipe(auditTime(100), takeUntil(this.destroy$))
        .subscribe(() => this.applyResolutionZoom());
    });

    this.route.events.subscribe((event) => {
      if (event.constructor.name === "NavigationEnd") {
        if (this.router.url !== "/login") {
          this.storageService.saveQueryFilters(
            {
              current_url: this.router.url
            },
            true
          );
        }
        setTimeout(this.applyResolutionZoom, 0);
      }
    });
  }

  goLink = (menu) => {
    if (menu.link) {
      // Si el menú tiene un enlace directo, navega a él
      this.route.navigateByUrl(menu.link);
    } else if (menu.submenu && menu.submenu.length > 0) {
      // Si el menú tiene un submenú, navega al primer enlace válido del submenú
      const firstValidSublink = menu.submenu.find((sub) => sub.link);
      if (firstValidSublink) {
        this.route.navigateByUrl(firstValidSublink.link);
      }
    }
  };
  isValid = (link: string) => {
    const currentUrl = this.route.url;
    return currentUrl.includes(link);
  };

  getCycleStatus = async (
    farm_id: number,
    validate_cycle: boolean
  ): Promise<{ cycles: submitCycle[]; status: 1 | 2 | 3 | 4 | 5 }> => {
    if (!this.storageService.isAdmin() && validate_cycle) {
      const data = await this.cyclesService.getCurrentStatusCycles(farm_id);
      this.permissionsService.status_cycle = data.status;
      return { cycles: data.cycles, status: data.status };
    }
    return { cycles: [], status: 1 };
  };
  setMenu = async (options) => {
    const { farm_id, redirect = false, validate_cycle = false } = options;
    let Localuser: any = localStorage.getItem("USER");
    let user: any = Localuser ? JSON.parse(Localuser) : "";

    if (user) {
      const { mode } = this.storageService.localValue;
      const client_permissions = this.permissionsService.client_permissions || null;
      const my_permissions = this.permissionsService.all_permissions;

      const menu = Menu.menu({
        user,
        mode,
        client_permissions,
        my_permissions,
        farm_id,
        status_cycle: (await this.getCycleStatus(farm_id, validate_cycle)).status
      });
      // Función para navegar a un enlace
      if (redirect) {
        // Buscar el primer elemento del menú que coincida con la URL actual
        const menuItem = menu.find((item) => {
          if (item.submenu) {
            // Si el elemento tiene un submenú, verifica si alguno de sus enlaces es válido
            return item.submenu.some((sub) => this.isValid(sub.link));
          }
          // Si no tiene submenú, verifica si su propio enlace es válido
          return this.isValid(item.link);
        });
        if (!menuItem) {
          if (menu.length > 0) {
            if (menu[0].link) {
              this.goLink(menu[0]);
            } else if (menu[0].submenu && menu[0].submenu.length > 0) {
              this.goLink(menu[0].submenu[0]);
            }
          }
        }
      }
      this.menuService.push(menu);
      this.storageService.setItem("sidebar_section", true);
    }
  };
}
